import type { ImportHistoryEntry } from "@/api/researchCms.service";
import { ROUTES } from "@/routes";
import { MOVE_NEEDS_DELETE_REASON } from "../components/BpaImport/importText";
import { ARCHIVE_NEEDS_DELETE_REASON } from "./bpaArchive";
import { ALL_KINDS, type ImportCounts, type ImportKind, NO_FIRM_KEY } from "./bpaImport";
import { importBlockedReason, isEditionLocked, moveBlockedReason } from "./bpaImportEditionStatus";
import { writeImportUrl } from "./bpaImportUrlState";
import { firmNameEntries } from "./firmNames";

export const SPLIT_NEEDS_DELETE_REASON =
  "Splitting moves content out of the original and needs delete permission.";
export const SPLIT_IN_MERGE_REASON =
  "This practice area is in a merge. Finish it, or remove its practice areas from the merge, before splitting.";
export const SPLIT_HISTORY_LOADING_REASON = "Loading import history.";
export const SPLIT_HISTORY_FAILED_REASON =
  "Import history couldn't be loaded, so a split can't be started.";
export const SPLIT_CHOOSE_BOTH_REASON =
  "Choose both new practice areas before archiving, so each one gets split history.";
export const SPLIT_BOTH_HALVES_ARCHIVED_REASON = (name: string) =>
  `Both new practice areas are archived. Archive ${name} from its own page.`;
export function splitMoveLine(originalName: string): string {
  return `A split moves every content type out of ${originalName}. Firms you untick stay there.`;
}
export const SPLIT_ARCHIVE_WARNING =
  "Are you sure you want to archive it? This will hide it from the website and all related applications.";

export const SPLIT_STILL_IN_FAILED_REASON = (name: string) =>
  `Couldn't load what is still in ${name}, so it can't be archived yet.`;

const EMPTY_HALF_HISTORY_NOTE = "It gets split history without content.";
const CARD_COUNT = 2;
const STILL_IN_PREVIEW_LIMIT = 6;
const MOVED_NAMES_SHOWN = 2;

export type HistoryState = "loading" | "failed" | "ready";

export type SplitEntryInput = {
  flagOn: boolean;
  canEdit: boolean;
  canDelete: boolean;
  archived: boolean;
  editionStatus: string | null;
  splitHasMoved: boolean;
  inMerge: boolean;
  historyState: HistoryState;
};

export function splitEntryState(input: SplitEntryInput): {
  visible: boolean;
  disabledReason: string | null;
} {
  if (!input.flagOn || !input.canEdit || input.archived) {
    return { visible: false, disabledReason: null };
  }
  if (!input.canDelete) return { visible: true, disabledReason: SPLIT_NEEDS_DELETE_REASON };
  if (input.inMerge) return { visible: true, disabledReason: SPLIT_IN_MERGE_REASON };
  if (input.historyState === "loading") {
    return { visible: true, disabledReason: SPLIT_HISTORY_LOADING_REASON };
  }
  if (input.historyState === "failed") {
    return { visible: true, disabledReason: SPLIT_HISTORY_FAILED_REASON };
  }
  const lockedReason = input.splitHasMoved ? null : moveBlockedReason(input.editionStatus);
  return { visible: true, disabledReason: lockedReason };
}

export function splitNameProblem(
  first: string,
  second: string,
  existingNames: string[],
): string | null {
  const firstName = first.trim();
  const secondName = second.trim();
  if (!firstName || !secondName) return "Give both new practice areas a name.";
  if (firstName.toLowerCase() === secondName.toLowerCase()) {
    return "The two names must be different.";
  }
  const taken = existingNames.map(name => name.trim().toLowerCase());
  const used = [firstName, secondName].find(name => taken.includes(name.toLowerCase()));
  return used ? `${used} is already a practice area name in this edition.` : null;
}

export type RankingListShape = {
  name: string;
  subtype: string;
  isTiered?: number;
  submissionType?: string | null;
  refereeType?: string | null;
};

export function splitRankingTables(
  originalLists: RankingListShape[],
  originalName: string,
  newName: string,
): RankingListShape[] {
  return originalLists.map(list =>
    list.name === originalName ? { ...list, name: newName } : list,
  );
}

export type SplitCardInput = {
  editionBpaId: string | null;
  bpaId: string | null;
  name: string | null;
};

export type StillInRow = { key: string; name: string; noFirm: boolean; counts: ImportCounts };

export type SplitCard = {
  editionBpaId: string | null;
  bpaId: string | null;
  name: string | null;
  needsChoosing: boolean;
  archived: boolean;
  moved: boolean;
  movedCount: number;
  movedAt: string | null;
  movedAnything: boolean;
  movedNames: string | null;
  movedNamesUnavailable: boolean;
  moveInVisible: boolean;
};

export type SplitHub = {
  cards: SplitCard[];
  doneCount: number;
  progress: string;
  nothingLeft: boolean;
  archiveOffered: boolean;
  archiveIsMain: boolean;
};

type TargetSummary = {
  card: SplitCardInput;
  companies: Map<string, string | null>;
  newestAt: string;
  movedAnything: boolean;
};

function summariseSplitTargets(
  history: ImportHistoryEntry[],
  originalEditionBpaId: string,
): Map<string, TargetSummary> {
  const splitEntries = history
    .filter(item => item.purpose === "split" && item.sourceEditionBpaId === originalEditionBpaId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const targets = new Map<string, TargetSummary>();
  for (const item of splitEntries) {
    const summary = targets.get(item.targetEditionBpaId) ?? {
      card: {
        editionBpaId: item.targetEditionBpaId,
        bpaId: item.targetBpaId,
        name: item.targetBpaName,
      },
      companies: new Map<string, string | null>(),
      newestAt: item.createdAt,
      movedAnything: false,
    };
    if (item.importedSubmissions + item.importedRankings + item.importedCommentary > 0) {
      summary.movedAnything = true;
    }
    for (const company of item.copiedCompanies) {
      summary.movedAnything = true;
      if (!summary.companies.has(company.id)) summary.companies.set(company.id, company.name);
    }
    targets.set(item.targetEditionBpaId, summary);
  }
  return targets;
}

function movedNamesText(companies: Map<string, string | null>): string | null {
  const { shown, more } = firmNameEntries([...companies.values()], MOVED_NAMES_SHOWN);
  if (shown.length === 0) return null;
  return more > 0 ? `${shown.join(", ")} and ${more} more` : shown.join(", ");
}

function chooseCardInputs(
  cardsFromUrl: SplitCardInput[],
  targets: Map<string, TargetSummary>,
): SplitCardInput[] {
  const chosen: SplitCardInput[] = [];
  const addCard = (card: SplitCardInput) => {
    if (!card.editionBpaId) return;
    if (chosen.some(existing => existing.editionBpaId === card.editionBpaId)) return;
    chosen.push(card);
  };
  for (const card of cardsFromUrl) addCard(card);
  for (const summary of targets.values()) addCard(summary.card);
  const limited = chosen.slice(0, CARD_COUNT);
  while (limited.length < CARD_COUNT) limited.push({ editionBpaId: null, bpaId: null, name: null });
  return limited;
}

export function splitHubModel(input: {
  originalEditionBpaId: string;
  history: ImportHistoryEntry[];
  cardsFromUrl: SplitCardInput[];
  stillIn: StillInRow[] | null;
  originalArchived?: boolean;
  archivedEditionBpaIds?: string[];
  firmNamesUnavailable?: boolean;
}): SplitHub {
  const targets = summariseSplitTargets(input.history, input.originalEditionBpaId);
  const archivedIds = input.archivedEditionBpaIds ?? [];
  const nothingLeft = input.stillIn !== null && input.stillIn.length === 0;
  const cards = chooseCardInputs(input.cardsFromUrl, targets).map((card): SplitCard => {
    const summary = card.editionBpaId ? targets.get(card.editionBpaId) : undefined;
    const needsChoosing = card.editionBpaId === null;
    const archived = card.editionBpaId !== null && archivedIds.includes(card.editionBpaId);
    return {
      editionBpaId: card.editionBpaId,
      bpaId: card.bpaId ?? summary?.card.bpaId ?? null,
      name: card.name ?? summary?.card.name ?? null,
      needsChoosing,
      archived,
      moved: !!summary,
      movedCount: summary?.companies.size ?? 0,
      movedAt: summary?.newestAt ?? null,
      movedAnything: summary?.movedAnything ?? false,
      movedNames: summary ? movedNamesText(summary.companies) : null,
      movedNamesUnavailable: !!summary && !!input.firmNamesUnavailable,
      moveInVisible: !nothingLeft && !needsChoosing && !archived && !input.originalArchived,
    };
  });
  const doneCount = cards.filter(card => card.moved).length;
  return {
    cards,
    doneCount,
    progress: `${doneCount} of ${CARD_COUNT} done`,
    nothingLeft,
    archiveOffered: !input.originalArchived && (nothingLeft || doneCount > 0),
    archiveIsMain: !input.originalArchived && nothingLeft,
  };
}

export function stillInFromSummary(
  firms: Array<{
    companyId: string | null;
    name: string | null;
    submissions: number;
    rankings: number;
    commentary: number;
  }>,
): StillInRow[] {
  return firms
    .filter(firm => firm.submissions + firm.rankings + firm.commentary > 0)
    .map(
      (firm): StillInRow => ({
        key: firm.companyId ?? NO_FIRM_KEY,
        name: firm.companyId === null ? "No firm" : (firm.name ?? "Unnamed organisation"),
        noFirm: firm.companyId === null,
        counts: {
          submissions: firm.submissions,
          rankings: firm.rankings,
          commentary: firm.commentary,
        },
      }),
    );
}

export function stillInUnavailableText(input: {
  editionStatus: string | null;
  originalArchived: boolean;
  originalName: string;
}): string {
  if (isEditionLocked(input.editionStatus)) {
    return `What is still in ${input.originalName} couldn't be loaded.`;
  }
  if (input.originalArchived) {
    return `${input.originalName} is archived, so what is still in it can't be checked.`;
  }
  return `Choose a practice area above to see what is still in ${input.originalName}.`;
}

export function previewAllowed(input: {
  editionStatus: string | null;
  originalArchived: boolean;
}): boolean {
  return !isEditionLocked(input.editionStatus) && !input.originalArchived;
}

export function previewTargetId(input: {
  cards: SplitCardInput[];
  practiceAreas: Array<{ id: string; status: string }>;
  originalEditionBpaId: string;
}): string | null {
  const statusOf = (id: string) => input.practiceAreas.find(area => area.id === id)?.status;
  const fromCard = input.cards.find(
    card => card.editionBpaId && statusOf(card.editionBpaId) !== "archived",
  )?.editionBpaId;
  if (fromCard) return fromCard;
  return (
    input.practiceAreas.find(
      area => area.id !== input.originalEditionBpaId && area.status !== "archived",
    )?.id ?? null
  );
}

export function stillInView(
  rows: StillInRow[],
  showAll: boolean,
  originalName: string,
): { rows: StillInRow[]; showAllLabel: string | null; emptyText: string | null } {
  if (rows.length === 0) {
    return { rows, showAllLabel: null, emptyText: `Nothing is left in ${originalName}.` };
  }
  if (showAll || rows.length <= STILL_IN_PREVIEW_LIMIT) {
    return { rows, showAllLabel: null, emptyText: null };
  }
  return {
    rows: rows.slice(0, STILL_IN_PREVIEW_LIMIT),
    showAllLabel: `Show all ${rows.length} · ${rows.length - STILL_IN_PREVIEW_LIMIT} more`,
    emptyText: null,
  };
}

export function splitHalves(
  original: { id: string; rankingEntityId: string },
  rows: { id: string; bpaId: string; bpaName: string; rankingEntityId: string; status: string }[],
): SplitCardInput[] {
  return rows
    .filter(
      row =>
        row.id !== original.id &&
        row.rankingEntityId === original.rankingEntityId &&
        row.status !== "archived",
    )
    .slice(0, 2)
    .map(row => ({ editionBpaId: row.id, bpaId: row.bpaId, name: row.bpaName }));
}

export function splitHubUrl(
  originalEditionId: string,
  originalBpaId: string,
  cards: SplitCardInput[],
): string {
  const params = new URLSearchParams();
  cards.forEach((card, index) => {
    if (!card.editionBpaId) return;
    const slot = index + 1;
    params.set(`card${slot}`, card.editionBpaId);
    if (card.bpaId) params.set(`card${slot}Bpa`, card.bpaId);
    if (card.name) params.set(`card${slot}Name`, card.name);
  });
  const path = ROUTES.research.cmsBpaSplit(originalEditionId, originalBpaId);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function readSplitCardsFromUrl(params: URLSearchParams): SplitCardInput[] {
  return Array.from({ length: CARD_COUNT }, (_, index) => {
    const slot = index + 1;
    return {
      editionBpaId: params.get(`card${slot}`),
      bpaId: params.get(`card${slot}Bpa`),
      name: params.get(`card${slot}Name`),
    };
  });
}

export function splitImportUrl(input: {
  targetEditionId: string;
  targetBpaId: string;
  sourceEditionBpaId: string;
  sourceEditionId: string;
  originalEditionId: string;
  originalBpaId: string;
  cards: SplitCardInput[];
}): string {
  const params = writeImportUrl(
    {
      sourceEditionBpaId: input.sourceEditionBpaId,
      sourceEditionId: input.sourceEditionId,
      kinds: ALL_KINDS,
      mode: "move",
      purpose: "split",
      hub: splitHubUrl(input.originalEditionId, input.originalBpaId, input.cards),
    },
    input.targetEditionId,
  );
  return `${ROUTES.research.cmsBpaImport(input.targetEditionId, input.targetBpaId)}?${params.toString()}`;
}

export function isSplitHubForImport(
  hub: string,
  sourceEditionBpa: { editionId: string; bpaId: string },
): boolean {
  if (!hub.startsWith("/") || hub.startsWith("//")) return false;
  const path = hub.split(/[?#]/)[0];
  return path === ROUTES.research.cmsBpaSplit(sourceEditionBpa.editionId, sourceEditionBpa.bpaId);
}

export function splitImportHint(otherCardMoved: boolean, otherName: string | null): string | null {
  if (otherCardMoved) return null;
  return `Untick the organisations that belong in ${otherName ?? "the other half"}.`;
}

export function splitUntickedLine(otherCardMoved: boolean, otherName: string | null): string {
  if (otherCardMoved) return "Unticked · they stay in the original, and archiving hides them";
  return `Unticked · they stay in the original for ${otherName ?? "the other half"}`;
}

export function splitRemovesText(count: number): string {
  return `Removes ${count} source ${count === 1 ? "item" : "items"}`;
}

export function archivePlan(input: {
  originalName: string;
  stillIn: StillInRow[] | null;
  cards: SplitCard[];
  archivedEditionBpaIds?: string[];
}): {
  confirmation: string[];
  newEditionBpaIds: string[];
  blockedReason: string | null;
} {
  const archivedIds = input.archivedEditionBpaIds ?? [];
  const activeCards = input.cards.filter(
    card => !card.editionBpaId || !archivedIds.includes(card.editionBpaId),
  );
  const confirmation: string[] = [];
  if (input.stillIn === null) {
    confirmation.push(
      "What is still in the original could not be checked, so it isn't listed here. Archiving hides it.",
    );
  } else if (input.stillIn.length > 0) {
    const names = input.stillIn.map(item => item.name).join(", ");
    confirmation.push(`Still in the original: ${names}. Archiving hides them.`);
  }
  for (const card of activeCards) {
    if (card.editionBpaId && !card.moved) {
      confirmation.push(`${card.name} is empty and stays as it is. ${EMPTY_HALF_HISTORY_NOTE}`);
    }
  }
  confirmation.push(SPLIT_ARCHIVE_WARNING);
  const newEditionBpaIds = activeCards.flatMap(card =>
    card.editionBpaId ? [card.editionBpaId] : [],
  );
  const blockedReason = input.cards.some(card => card.needsChoosing)
    ? SPLIT_CHOOSE_BOTH_REASON
    : newEditionBpaIds.length === 0
      ? SPLIT_BOTH_HALVES_ARCHIVED_REASON(input.originalName)
      : null;
  return { confirmation, newEditionBpaIds, blockedReason };
}

export type PickerPracticeArea = {
  editionBpaId: string;
  bpaId: string;
  name: string;
  status: string;
  hasContent: boolean;
  inMerge: boolean;
};

export function pickerCandidates(
  practiceAreas: PickerPracticeArea[],
  originalEditionBpaId: string,
  otherCardEditionBpaId: string | null,
): PickerPracticeArea[] {
  return practiceAreas.filter(
    area =>
      area.editionBpaId !== originalEditionBpaId &&
      area.editionBpaId !== otherCardEditionBpaId &&
      area.status !== "archived" &&
      !area.inMerge &&
      !area.hasContent,
  );
}

export function hubPermissions(input: {
  flagOn: boolean;
  canRead: boolean;
  canEdit: boolean;
  canDelete: boolean;
  editionStatus: string | null;
}): {
  open: boolean;
  viewOnly: boolean;
  moveInDisabledReason: string | null;
  archiveDisabledReason: string | null;
} {
  return {
    open: input.flagOn && input.canRead,
    viewOnly: !input.canEdit,
    moveInDisabledReason: input.canDelete
      ? importBlockedReason(input.editionStatus)
      : MOVE_NEEDS_DELETE_REASON,
    archiveDisabledReason: input.canDelete ? null : ARCHIVE_NEEDS_DELETE_REASON,
  };
}
